import { useQuery } from '@apollo/client';
import dashboardStatsQuery from 'wb-reddit/app/routes/superGrid/hooks/api/dashboardStats.graphql';
import {
	CampaignOrderByColumn, DashboardStatsQuery, DashboardStatsQueryVariables, IdFilter,
} from 'wb-reddit/graphql/generated';
import { DATE_PARSE_FORMAT, useUrlParams } from '@aleph/shared-components';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { breakdownChartMetrics } from 'wb-reddit/app/routes/superGrid/actionRow/charts/utils';
import { CellPosition } from 'ag-grid-community';
import { useNavigate, useParams } from 'react-router-dom';

type UseDashboardStatsParams = {
	parentFilter: Record<string, [IdFilter]>,
	focusedCell: CellPosition | null,
	selectedKPI?: string | null,
	skip?: boolean,
};

const useDashboardStats = ({
	parentFilter,
	focusedCell,
	selectedKPI,
	skip = false,
}: UseDashboardStatsParams) => {
	const navigate = useNavigate();

	const { params } = useUrlParams({ navigate, useParams });

	const [cachedData, setCachedData] = useState<DashboardStatsQuery>();

	const focusedCellColumn = useMemo(() => {
		const cell = focusedCell?.column.getColId();

		if (focusedCell?.column.getColId()) {
			const metric = breakdownChartMetrics.find(m => m === cell);

			if (metric) {
				return metric as CampaignOrderByColumn;
			}
		}

		return (selectedKPI ?? 'impressions') as CampaignOrderByColumn;
	}, [focusedCell]);

	const dateFrom = dayjs(params.date?.split(':')?.[0]);
	const dateTo = dayjs(params.date?.split(':')?.[1]);
	const dateDiff = dateTo.diff(dateFrom, 'days') + 1;

	const lastDate = `${dateFrom.subtract(dateDiff, 'days').format(DATE_PARSE_FORMAT)}:${dateTo.subtract(dateDiff, 'days').format(DATE_PARSE_FORMAT)}`;

	const {
		data: dashboardStats, loading,
	} = useQuery<DashboardStatsQuery, DashboardStatsQueryVariables>(dashboardStatsQuery, {
		variables: {
			filters: {
				...params.filters,
				...parentFilter,
				date: { value: params.date },
			},
			performerFilters:     parentFilter,
			currDateFilter:       { value: params.date },
			lastDateFilter:       { value: lastDate },
			performersSortColumn: focusedCellColumn,
		},
		returnPartialData: false,
		skip,
	});

	useEffect(() => {
		if (dashboardStats && !loading) {
			setCachedData(dashboardStats);
		}
	}, [dashboardStats]);

	return {
		reloading:       loading,
		loading:         loading && !cachedData,
		stats:           cachedData?.sparklineStats?.items ?? [],
		bestPerformers:  cachedData?.bestPerformers?.items ?? [],
		worstPerformers: cachedData?.worstPerformers?.items ?? [],
	};
};

export default useDashboardStats;
